/**
 * A character sheet, as bytes.
 *
 * Who somebody is, before gear and before the moment: four earned stats and a table of perk ranks.
 * The host steps every body in the session, so it needs every guest's sheet, and a guest predicts
 * its own body, so the two have to be stepping the same one. This is how it crosses.
 *
 * It sits beside the frames rather than inside one because two lobby messages carry it: a guest
 * announcing what it is, and the roster the host publishes back. A field shared by two messages is
 * a field with its own file and its own round-trip tests.
 *
 * ```text
 * byte 0..3  the four stats, one byte each, in STAT_ORDER
 * byte 4..7  experience, u32
 * byte 8     how many perks follow
 * then       PERK_ORDER index, rank, two bytes each, ascending by index
 * ```
 *
 * A perk is named by its position in `PERK_ORDER` rather than by its id, because a string id per
 * perk would put a maxed sheet past four hundred bytes and a four-seat roster past any frame budget
 * worth having. The cost is that the ordering is part of the layout: inserting or reordering a perk
 * changes what every index means, so it is a protocol change and `PERK_ORDER` is pinned by a test
 * that says so. Both peers already share a build's ordering, because a version mismatch is refused
 * at the handshake.
 *
 * Ids are refused and magnitudes are clamped, the same rule a save goes in under. Stats go through
 * `statsFrom`, which clamps and cannot fail, and the progress record goes through the very
 * `validateProgress` a profile off disk goes through, rather than through a second gate here that
 * could drift from it.
 */

import { NetError } from '@/net/session/sessionErrors';
import { PERK_ORDER } from '@/sim/progression/perkRegistry';
import { STAT_ORDER, statValue, statsFrom } from '@/sim/combat/statContract';
import { validateProgress } from '@/sim/progression/progressState';
import type { ByteReader, ByteWriter } from '@/net/wire/byteCursor';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { StatId } from '@/sim/combat/statContract';

/** The stats and the experience. Everything before the perk table. */
const SHEET_FIXED_BYTES = STAT_ORDER.length + 4 + 1;

const PERK_ENTRY_BYTES = 2;

/** A sheet with every perk taken. What a roster has to budget for, per seat. */
export const MAX_SHEET_BYTES = SHEET_FIXED_BYTES + PERK_ORDER.length * PERK_ENTRY_BYTES;

export function writeSheet(writer: ByteWriter, sheet: CharacterSheet): void {
  // Walked in STAT_ORDER and PERK_ORDER rather than in key order, so the same sheet is the same
  // bytes on every machine and a roster frame does not change shape with whoever built the object.
  for (const id of STAT_ORDER) writer.u8(statValue(sheet.stats, id));

  writer.u32(sheet.progress.xp);

  const taken = PERK_ORDER.map((id, index) => ({
    index,
    rank: sheet.progress.perkRanks[id] ?? 0,
  })).filter((entry) => entry.rank > 0);

  if (taken.length > 0xff) {
    throw new NetError('malformedFrame', 'a perk table has to fit in a byte of length');
  }

  writer.u8(taken.length);
  for (const entry of taken) {
    writer.u8(entry.index);
    writer.u8(entry.rank);
  }
}

/** Throws on anything it cannot read in full. A half-read sheet is a body stepped from a lie. */
export function readSheet(reader: ByteReader): CharacterSheet {
  // Mutable and partial on the way in, so the block is filled in STAT_ORDER rather than written
  // out a second time here, and `statsFrom` is still the one thing that decides what a stat may be.
  const stats: Partial<Record<StatId, number>> = {};

  for (const id of STAT_ORDER) stats[id] = reader.u8();

  const xp = reader.u32();
  const count = reader.u8();
  const ranks: Record<string, number> = {};

  for (let entry = 0; entry < count; entry += 1) {
    const index = reader.u8();
    const rank = reader.u8();
    const id = PERK_ORDER[index];

    if (id === undefined) {
      throw new NetError('malformedFrame', `perk index ${String(index)} is not one this build has`);
    }

    ranks[id] = rank;
  }

  const progress = validateProgress(xp, ranks);

  if (!progress.ok) {
    throw new NetError(
      'malformedFrame',
      `a character sheet was refused: ${progress.failure.reason}`,
    );
  }

  return { stats: statsFrom(stats), progress: progress.value };
}
